import { getCurrentUser } from "@/services/auth.service";
import { getAuthToken, removeAuthToken, setAuthToken } from "@/services/api";
import type { AuthUser } from "@/types/auth.type";
import { createContext, useContext, useEffect, useState } from "react";

// ========================================
// 1️⃣ DÉFINIR LE TYPE DU CONTEXTE
// ========================================
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void; // logout()
  loading: boolean;
}

// ========================================
// 2️⃣ CRÉER LE CONTEXTE
// ========================================
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ========================================
// 3️⃣ CRÉER LE PROVIDER (fournisseur)
// ========================================
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setIsloading] = useState<boolean>(true);

  // ========================================
  // 4️⃣ CHARGER L'UTILISATEUR AU DÉMARRAGE
  // ========================================
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = getAuthToken();
        
        if (token) {
          // Appeler l'API pour récupérer le profil complet
          const user = await getCurrentUser();
          
          // Sauvegarder dans localStorage
          localStorage.setItem("user", JSON.stringify(user));
          
          // Mettre à jour l'état
          setUser(user);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du user: ", error);
        removeAuthToken();
      } finally {
        setIsloading(false);
      }
    };
  
    loadUser();
  }, []);

  // ========================================
  // 5️⃣ FONCTION LOGIN
  // ========================================
  const login = async (token: string) => {
    try {
    // Sauvegarder le token dans localStorage
    setAuthToken(token);
    // Sauvegarder les infos utilisateur dans localStorage
    const user= await getCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
    } catch (error) {
      removeAuthToken();
      throw error;
    }
  };

  // ========================================
  // 6️⃣ FONCTION LOGOUT
  // ========================================
  const logout = () => {
    removeAuthToken();
    setUser(null);
    window.location.href = "/login";
  };

  // ========================================
  // 7️⃣ VALEUR DU CONTEXTE
  // ========================================
  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
  };

  // ========================================
  // 8️⃣ FOURNIR LE CONTEXTE À TOUS LES ENFANTS
  // ========================================
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ========================================
// 9️⃣ HOOK PERSONNALISÉ POUR UTILISER LE CONTEXTE
// ========================================
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      "useAuth doit être utilisé à l'intérieur d'un AuthProvider"
    );
  }
  return context;
};
